/* ──────────────────────────────────────────────────────────────────────────
 * Registered User – Advisor: the returning IPO-professional command centre.
 * Built from "returning_advisor.html". Identity (name/firm) is pulled LIVE from
 * the user's context; inbound mandates / owned chapters are realistic SAMPLE
 * data (clearly labelled) until the inbound-mandate backend exists. Scoped `.av`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import RoleSwitcher from "@/components/RoleSwitcher";
import { getContext } from "@/lib/roleContext";
import { logJourney } from "@/lib/journey";
import "./RegisteredAdvisor.css";

const initialsOf = (s) => (s || "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "AD";
const firstNameOf = (n) => (n || "").trim().split(/\s+/)[0] || "there";

const API = process.env.REACT_APP_BACKEND_URL;
const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return d === 1 ? "1 day ago" : `${d} days ago`;
};

/* Enquiry thread — advisor reads a Customer Enquiry and replies in-app; the
 * reply is stored in the thread and emailed to the person who enquired. */
const ThreadModal = ({ msg, onClose, onSent }) => {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!msg) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/matchmaker/expert/messages/thread/${msg.message_id}`, { credentials: "include" });
        if (r.ok) setThread(await r.json());
      } catch { /* ignore */ }
    })();
  }, [msg]);
  if (!msg) return null;
  const orig = thread?.original || msg;
  const send = async () => {
    if (!text.trim()) { toast.error("Please write a reply."); return; }
    setSending(true);
    try {
      const r = await fetch(`${API}/api/matchmaker/expert/messages/${msg.message_id}/reply`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: text }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Could not send reply."); setSending(false); return; }
      toast.success(d.emailed ? "Reply sent and emailed to the enquirer." : "Reply saved to the thread.");
      setThread((t) => ({ original: t?.original || orig, replies: [...(t?.replies || []), d.reply] }));
      setText("");
      setSending(false);
      onSent && onSent();
    } catch { toast.error("Connection error."); setSending(false); }
  };
  return (
    <div className="enq-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="enq-modal" data-testid="enq-thread-modal">
        <div className="enq-modal-head">
          <div>
            <div className="enq-modal-t">{orig.from_name}</div>
            <div className="enq-modal-s">{orig.from_email}{orig.from_mobile ? ` · ${orig.from_mobile}` : ""}</div>
          </div>
          <button className="enq-x" onClick={onClose} data-testid="enq-thread-close" aria-label="Close"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
        <div className="enq-thread">
          <div className="bub bub-in"><div className="bub-meta">{orig.from_name} · {relTime(orig.created_at)}</div>{orig.body}</div>
          {(thread?.replies || []).map((rp) => (
            <div className="bub bub-out" key={rp.message_id}><div className="bub-meta">You · {relTime(rp.created_at)}</div>{rp.body}</div>
          ))}
        </div>
        <div className="enq-composer">
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={`Reply to ${orig.from_name}…`} data-testid="enq-reply-input" />
          <button className="enq-send" disabled={sending} onClick={send} data-testid="enq-reply-send">{sending ? "Sending…" : "Send reply"}</button>
        </div>
        <p className="enq-note"><i className="ti ti-info-circle" aria-hidden="true" /> Your reply is emailed to {orig.from_email} and kept here in the thread.</p>
      </div>
    </div>
  );
};

export const RegisteredAdvisor = () => {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [showSample, setShowSample] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [openMsg, setOpenMsg] = useState(null);

  const loadInbox = async () => {
    try {
      const r = await fetch(`${API}/api/matchmaker/expert/messages/inbox`, { credentials: "include" });
      const d = r.ok ? await r.json() : { messages: [], unread_count: 0 };
      setEnquiries(d.messages || []);
      setUnread(d.unread_count || 0);
    } catch { /* ignore */ } finally { setLoadingInbox(false); }
  };

  useEffect(() => { getContext().then(setCtx); logJourney("Advisor Marketplace", "Viewed", "Opened Registered Advisor workspace"); loadInbox(); }, []);

  const firm = ctx?.identity?.advisorFirm || ctx?.name || "Your practice";
  const initials = initialsOf(firm);
  const sample = () => toast.info("Sample data — live inbound mandates will appear here as companies engage you in the Advisor Marketplace.");

  return (
    <div className="av" data-testid="registered-advisor-page">
      <div className="hd">
        <div className="brand" onClick={() => navigate("/home")} style={{ cursor: "pointer" }} data-testid="ra-brand">IPO<span>Labs</span></div>
        <div className="hnav">
          <button className="hnav-b home" onClick={() => navigate("/home")} data-testid="home-page-btn"><i className="ti ti-home" aria-hidden="true" /> Home Page</button>
          <button className="hnav-b oracle" onClick={() => navigate("/oracle")} data-testid="ask-oracle-btn"><i className="ti ti-robot" aria-hidden="true" /> Ask Oracle {"{IPO.GPT}"}</button>
        </div>
        <div className="gs"><i className="ti ti-search" aria-hidden="true" /><input placeholder="Search mandates, clients, chapters…" onKeyDown={(e) => e.key === "Enter" && navigate("/advisor-marketplace")} /></div>
        <div className="hd-r">
          <span className="hic" onClick={sample}><i className="ti ti-bell" aria-hidden="true" /><span className="hbadge">3</span></span>
          <RoleSwitcher accent="#0F6E56" currentRole="advisor" label={firm} initials={initials} />
        </div>
      </div>

      <div className="hero">
        <div className="hero-l">
          <div className="hero-kick">Advisor workspace · IPO professional</div>
          <div className="hero-h">Welcome back, {firm}.</div>
          <div className="hero-d">This is your advisor command centre — inbound mandate requests, the DRHP chapters you own across client IPOs, your ratings and pending tasks, all in one place.</div>
          <div className="hero-cta">
            <button className="hbtn-w" onClick={() => navigate("/advisor-marketplace")} data-testid="ra-review-mandates">Go to Marketplace →</button>
            <button className="hbtn-o" onClick={sample} data-testid="ra-my-chapters">My chapters</button>
          </div>
        </div>
        <div className="prof">
          <div className="prof-row"><div className="prof-av">{initials}</div><div><div className="prof-n">{firm} <i className="ti ti-rosette-discount-check" style={{ fontSize: 12 }} aria-hidden="true" /></div><div className="prof-r">Verified advisor · Advisor Marketplace</div></div></div>
          <div className="prof-stats">
            <div><div className="prof-v">4.9</div><div className="prof-l">rating</div></div>
            <div><div className="prof-v">24</div><div className="prof-l">SME IPOs</div></div>
            <div><div className="prof-v">3</div><div className="prof-l">active</div></div>
          </div>
        </div>
      </div>

      {showSample && (
        <div className="sample-banner" data-testid="ra-sample-banner">
          <i className="ti ti-flask" aria-hidden="true" />
          <span>The mandates, chapters and earnings below are <b>sample data</b> to show your advisor workspace. Your real inbound mandates appear here as companies engage you.</span>
          <button onClick={() => navigate("/advisor-marketplace")} data-testid="ra-sample-explore">Open my Marketplace profile →</button>
          <button className="sample-x" onClick={() => setShowSample(false)} data-testid="ra-sample-dismiss" aria-label="Dismiss sample data banner"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
      )}

      <div className="kpis">
        <div className="kk" data-testid="ra-kpi-mandates"><div className="kk-ic" style={{ color: "#0F6E56" }}><i className="ti ti-inbox" aria-hidden="true" /></div><div className="kk-v">{enquiries.length}</div><div className="kk-l">Customer enquiries</div><div className="kk-sub" style={{ color: unread > 0 ? "#0F6E56" : "#8a8a94" }}>{unread > 0 ? `${unread} unread` : "all caught up"}</div></div>
        <div className="kk" data-testid="ra-kpi-chapters"><div className="kk-ic" style={{ color: "#6C4BF4" }}><i className="ti ti-file-text" aria-hidden="true" /></div><div className="kk-v">4</div><div className="kk-l">Chapters need you</div><div className="kk-sub" style={{ color: "#d97706" }}>1 sign-off due</div></div>
        <div className="kk" data-testid="ra-kpi-active"><div className="kk-ic" style={{ color: "#185FA5" }}><i className="ti ti-briefcase" aria-hidden="true" /></div><div className="kk-v">3</div><div className="kk-l">Active engagements</div><div className="kk-sub" style={{ color: "#8a8a94" }}>Acme, Nirman, Vector</div></div>
        <div className="kk" data-testid="ra-kpi-billed"><div className="kk-ic" style={{ color: "#a86711" }}><i className="ti ti-coin" aria-hidden="true" /></div><div className="kk-v">₹6.8L</div><div className="kk-l">Billed this quarter</div><div className="kk-sub" style={{ color: "#d97706" }}>₹4.25L pending</div></div>
      </div>

      <div className="body">
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-t"><i className="ti ti-inbox" style={{ color: "#0F6E56" }} aria-hidden="true" /> Customer enquiries {unread > 0 && <span className="enq-unread" data-testid="enq-unread-badge">{unread} new</span>} <span className="more" onClick={() => navigate("/advisor-marketplace")}>My profile →</span></div>
            {loadingInbox ? (
              <div className="enq-empty" data-testid="enq-loading">Loading enquiries…</div>
            ) : enquiries.length === 0 ? (
              <div className="enq-empty" data-testid="enq-empty">No enquiries yet. When someone messages you from the Advisor Marketplace, their enquiry lands here — and you can reply without leaving the page.</div>
            ) : enquiries.map((m) => (
              <div className="enq" key={m.message_id} onClick={() => setOpenMsg(m)} data-testid={`enq-${m.message_id}`}>
                <div className="enq-top">
                  <div className="enq-av">{initialsOf(m.from_name)}</div>
                  <div className="enq-main">
                    <div className="enq-n">{m.from_name} {!m.is_read && <span className="enq-dot" title="Unread" />}</div>
                    <div className="enq-sub">{m.subject}</div>
                  </div>
                  <div className="enq-time">{relTime(m.created_at)}</div>
                </div>
                <div className="enq-preview">{m.body}</div>
                <div className="enq-foot">
                  <span className="enq-meta"><i className="ti ti-mail" aria-hidden="true" /> {m.from_email}</span>
                  {m.reply_count > 0 && <span className="enq-replied"><i className="ti ti-corner-up-left" aria-hidden="true" /> {m.reply_count} repl{m.reply_count > 1 ? "ies" : "y"}</span>}
                  <button className="enq-reply-b" onClick={(e) => { e.stopPropagation(); setOpenMsg(m); }} data-testid={`enq-open-${m.message_id}`}>Open &amp; reply →</button>
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-t"><i className="ti ti-file-text" style={{ color: "#0F6E56" }} aria-hidden="true" /> Chapters I own <span className="more" onClick={sample}>All clients →</span></div>
            {[{ c: "cs-rev", n: "Restated Financials", co: "Acme Seeds", go: "Sign off →" },
              { c: "cs-prog", n: "MD&A", co: "Acme Seeds", go: "Edit →" },
              { c: "cs-prog", n: "Capitalisation", co: "Nirman Tools", go: "Edit →" },
              { c: "cs-done", n: "Indebtedness", co: "Vector Polymers", go: "View →" }].map((ch, i) => (
              <div className="chap" key={i} onClick={sample}><span className={`chap-dot ${ch.c}`} /><span className="chap-n">{ch.n} <span className="chap-co">· {ch.co}</span></span><span className="chap-go">{ch.go}</span></div>
            ))}
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-t"><i className="ti ti-checklist" style={{ color: "#0F6E56" }} aria-hidden="true" /> Tasks &amp; deadlines</div>
            <div className="act"><span className="act-dot" style={{ background: "#dc2626" }} /><div><div className="act-t"><b>Sign off Acme restated financials</b> — blocks their Stage 4</div><div className="act-time">Due this week</div></div></div>
            <div className="act"><span className="act-dot" style={{ background: "#d97706" }} /><div><div className="act-t"><b>Respond to Kotak's query</b> on FY25 GST reconciliation</div><div className="act-time">2 days</div></div></div>
            <div className="act"><span className="act-dot" style={{ background: "#6C4BF4" }} /><div><div className="act-t"><b>Nirman Tools</b> capitalisation table review</div><div className="act-time">Next week</div></div></div>
          </div>

          <div className="card">
            <div className="card-t"><i className="ti ti-star" style={{ color: "#0F6E56" }} aria-hidden="true" /> Grow your practice</div>
            <div className="act" style={{ border: "none", paddingTop: 0, cursor: "pointer" }} onClick={() => navigate("/advisor-marketplace")}><span className="act-dot" style={{ background: "#0F6E56" }} /><div><div className="act-t">Complete your track record to rank <b>#1 for auditors</b> in your city</div><div className="act-time">Improve your ranking</div></div></div>
            <div className="act" style={{ border: "none", cursor: "pointer" }} onClick={() => navigate("/reseller-network")}><span className="act-dot" style={{ background: "#185FA5" }} /><div><div className="act-t">Refer SME clients &amp; earn <b>25% revenue share</b></div><div className="act-time">Reseller program</div></div></div>
          </div>
        </div>
      </div>

      <ThreadModal msg={openMsg} onClose={() => setOpenMsg(null)} onSent={loadInbox} />
    </div>
  );
};

export default RegisteredAdvisor;
